import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  IntellioAssistantWorkflowExecutionRecord,
  IntellioAssistantWorkflowStudioStateOutput,
  IntellioAssistantWorkflowTemplateRecord,
  IntellioUpsertAssistantWorkflowDraftInput,
} from '@triedge/domain-core/contracts';
import type { AssistantWorkflowGraphNode } from '@triedge/domain-core/entities';
import type { AssistantWorkflowStudioPageProps } from '../types';
import { SettingsBundleProvider, useSettingsBundle } from '../context/SettingsBundleContext';
import {
  ActionButton,
  DataTable,
  Field,
  KeyValueGrid,
  Notice,
  SelectInput,
  SettingsPanel,
  StatusPill,
  TextArea,
  TextInput,
  gridStyle,
  stackStyle,
} from '../components/ui';

const DEFAULT_TIMEZONE = 'America/New_York';

const asTriggerSummary = (
  studioState: IntellioAssistantWorkflowStudioStateOutput | undefined,
): string => {
  const trigger = studioState?.workflow?.triggerConfig ?? studioState?.defaultDraft?.triggerConfig;
  if (!trigger) return 'Not configured';
  if (trigger.kind === 'schedule') {
    return trigger.scheduleDescription ?? trigger.scheduleExpression;
  }
  if (trigger.kind === 'admissions_threshold') {
    return `${trigger.metric} >= ${trigger.threshold} (${trigger.window.replace(/_/g, ' ')})`;
  }
  return 'Manual';
};

const defaultTitleFor = (template: IntellioAssistantWorkflowTemplateRecord | null | undefined): string =>
  template ? `${template.title} Draft` : 'New Assistant Workflow';

export const AssistantWorkflowStudioPage = ({
  bundle,
  workflowId,
  workflowRouteBasePath,
}: AssistantWorkflowStudioPageProps) => (
  <SettingsBundleProvider value={bundle}>
    <AssistantWorkflowStudioPageContent
      workflowId={workflowId}
      workflowRouteBasePath={workflowRouteBasePath}
    />
  </SettingsBundleProvider>
);

const AssistantWorkflowStudioPageContent = ({
  workflowId,
  workflowRouteBasePath,
}: {
  workflowId: string;
  workflowRouteBasePath?: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedBundle = useSettingsBundle();
  const resolvedWorkflowRouteBasePath = workflowRouteBasePath
    ?? `${resolvedBundle.routeManifest.routeBase}/integrations/assistant-workflows`;
  const studioQuery = resolvedBundle.hooks.useGetAssistantWorkflowStudioStateQuery({
    workflowId,
    pathname: location.pathname,
  });
  const [upsertDraft, upsertDraftState] = resolvedBundle.hooks.useUpsertAssistantWorkflowDraftMutation();
  const [submitForApproval, submitForApprovalState] = resolvedBundle.hooks.useSubmitAssistantWorkflowForApprovalMutation();
  const [reviewWorkflow, reviewWorkflowState] = resolvedBundle.hooks.useReviewAssistantWorkflowMutation();
  const [mutateAssistantRun, mutateAssistantRunState] = resolvedBundle.hooks.useMutateAssistantRunMutation();

  const studioState = studioQuery.data as IntellioAssistantWorkflowStudioStateOutput | undefined;
  const templates = studioState?.templates ?? [];
  const existingWorkflow = studioState?.workflow ?? null;
  const initialTemplateId = existingWorkflow?.templateId ?? templates[0]?.id ?? '';

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(initialTemplateId);
  const [title, setTitle] = useState(existingWorkflow?.title ?? '');
  const [description, setDescription] = useState(existingWorkflow?.description ?? '');
  const [reviewComment, setReviewComment] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const preferredTemplateId = existingWorkflow?.templateId ?? templates[0]?.id ?? '';
    setSelectedTemplateId(preferredTemplateId);
    setTitle(existingWorkflow?.title ?? defaultTitleFor(templates[0]));
    setDescription(existingWorkflow?.description ?? '');
  }, [existingWorkflow?.description, existingWorkflow?.templateId, existingWorkflow?.title, templates]);

  const selectedTemplate = useMemo(
    () => templates.find((entry: IntellioAssistantWorkflowTemplateRecord) => entry.id === selectedTemplateId) ?? templates[0] ?? null,
    [selectedTemplateId, templates],
  );

  const baseUpsertInput = (): IntellioUpsertAssistantWorkflowDraftInput => ({
    workflowId: existingWorkflow?.id ?? undefined,
    templateId: selectedTemplate?.id ?? templates[0]?.id ?? 'admissions.dataset_to_spreadsheet_delivery',
    title: title.trim() || defaultTitleFor(selectedTemplate),
    description: description.trim() || undefined,
    routeScope: existingWorkflow?.routeScope ?? selectedTemplate?.routeScope ?? '/school/admissions',
    graphSpec: existingWorkflow?.graphSpec ?? selectedTemplate?.defaultGraphSpec ?? {
      version: 'v1',
      nodes: [{ id: 'trigger-manual', kind: 'manual', label: 'Run manually' }],
      edges: [],
    },
    connectorBindings: existingWorkflow?.connectorBindings ?? [],
    triggerConfig: existingWorkflow?.triggerConfig ?? studioState?.defaultDraft?.triggerConfig ?? { kind: 'manual' },
    timezone: existingWorkflow?.timezone ?? studioState?.defaultDraft?.timezone ?? DEFAULT_TIMEZONE,
    autonomousDelivery: existingWorkflow?.autonomousDelivery ?? false,
  });

  const saveDraft = async () => {
    try {
      const result = await upsertDraft(baseUpsertInput()).unwrap();
      const nextWorkflowId = String(result.workflow?.id ?? '').trim();
      setMessage(nextWorkflowId ? 'Workflow draft saved.' : 'Workflow draft updated.');
      setErrorMessage(null);
      await studioQuery.refetch();
      if (workflowId === 'new' && nextWorkflowId) {
        navigate(`${resolvedWorkflowRouteBasePath}/${nextWorkflowId}`, { replace: true });
      }
    } catch (error) {
      setMessage(null);
      setErrorMessage(error instanceof Error ? error.message : String(error ?? 'Unable to save workflow draft.'));
    }
  };

  const runWorkflow = async () => {
    if (!studioState?.workflow) return;
    try {
      await mutateAssistantRun({
        action: 'start',
        question: studioState.canonicalThread.launchQuestion,
        context: {
          module: 'settings',
          capabilityModule: 'settings',
          pathname: studioState.canonicalThread.launchRoute,
          assistantTask: {
            title: studioState.workflow.title,
            summary: studioState.workflow.description ?? studioState.workflow.title,
            workflowKey: studioState.workflow.templateId,
            workflowDefinitionId: studioState.workflow.id,
            executionSnapshotHash: studioState.workflow.executionSnapshotHash,
          },
        },
      }).unwrap();
      setMessage('Workflow execution requested.');
      setErrorMessage(null);
      await studioQuery.refetch();
    } catch (error) {
      setMessage(null);
      setErrorMessage(error instanceof Error ? error.message : String(error ?? 'Unable to start workflow.'));
    }
  };

  const pending =
    upsertDraftState.isLoading
    || submitForApprovalState.isLoading
    || reviewWorkflowState.isLoading
    || mutateAssistantRunState.isLoading;

  return (
    <section
      data-testid="assistant-workflow-studio-root"
      data-workflow-mode={studioState?.mode ?? 'loading'}
      data-workflow-ready={studioQuery.isSuccess && studioState?.workflow ? 'true' : 'false'}
      data-extracted-from="owned:staff-settings-ui/assistant-workflow-studio"
      style={stackStyle(18)}
    >
      {message ? <Notice tone="success" title="Workflow updated">{message}</Notice> : null}
      {errorMessage ? <Notice tone="error" title="Workflow action failed">{errorMessage}</Notice> : null}
      {studioQuery.isLoading ? <Notice title="Loading workflow studio">Fetching workflow studio state.</Notice> : null}
      {studioQuery.isError ? <Notice tone="error" title="Workflow studio unavailable">Unable to load the workflow studio right now.</Notice> : null}
      {studioQuery.isSuccess && workflowId !== 'new' && !studioState?.workflow ? (
        <Notice tone="warning" title="Workflow not found">
          This workflow id does not resolve to a visible draft in the current actor scope.
        </Notice>
      ) : null}

      <SettingsPanel
        eyebrow="Assistant Workflow"
        title={existingWorkflow?.title ?? defaultTitleFor(selectedTemplate)}
        description="Live package-owned assistant workflow management for draft, moderation, and execution operations."
        actions={(
          <>
            <ActionButton
              data-testid="assistant-workflow-save-button"
              disabled={pending || !selectedTemplate}
              onClick={() => void saveDraft()}
              variant="primary"
            >
              Save Draft
            </ActionButton>
            <ActionButton
              data-testid="assistant-workflow-run-button"
              disabled={pending || !studioState?.workflow}
              onClick={() => void runWorkflow()}
              variant="secondary"
            >
              Run Now
            </ActionButton>
          </>
        )}
      >
        <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <StatusPill tone={existingWorkflow?.approvalStatus === 'approved' ? 'success' : existingWorkflow?.approvalStatus === 'pending_approval' ? 'warning' : 'info'}>
            {existingWorkflow?.approvalStatus ?? studioState?.mode ?? 'draft'}
          </StatusPill>
          <StatusPill tone={studioState?.connectorReadiness.googleWorkspace.ready ? 'success' : 'warning'}>
            {studioState?.connectorReadiness.googleWorkspace.status ?? 'not_connected'}
          </StatusPill>
          <StatusPill tone="neutral">{asTriggerSummary(studioState)}</StatusPill>
        </div>

        <div style={gridStyle(2)}>
          <Field label="Template">
            <SelectInput
              data-testid="assistant-workflow-template-select"
              disabled={pending || Boolean(existingWorkflow)}
              onChange={(event) => setSelectedTemplateId(event.target.value)}
              value={selectedTemplateId}
            >
              {templates.map((template: IntellioAssistantWorkflowTemplateRecord) => (
                <option key={template.id} value={template.id}>
                  {template.title}
                </option>
              ))}
            </SelectInput>
          </Field>
          <Field label="Timezone">
            <TextInput
              data-testid="assistant-workflow-timezone-input"
              disabled
              value={existingWorkflow?.timezone ?? studioState?.defaultDraft?.timezone ?? DEFAULT_TIMEZONE}
            />
          </Field>
          <Field label="Title">
            <TextInput
              data-testid="assistant-workflow-title-input"
              onChange={(event) => setTitle(event.target.value)}
              value={title}
            />
          </Field>
          <Field label="Route Scope">
            <TextInput
              data-testid="assistant-workflow-route-scope-input"
              disabled
              value={existingWorkflow?.routeScope ?? selectedTemplate?.routeScope ?? '/school/admissions'}
            />
          </Field>
        </div>
        <Field label="Description">
          <TextArea
            data-testid="assistant-workflow-description-input"
            onChange={(event) => setDescription(event.target.value)}
            value={description}
          />
        </Field>
      </SettingsPanel>

      <div style={gridStyle(2)}>
        <SettingsPanel
          eyebrow="Authority"
          title="Viewer Authority"
          description="Live authority and connector posture for the current actor."
          actions={existingWorkflow ? (
            <ActionButton
              data-testid="assistant-workflow-submit-button"
              disabled={pending || existingWorkflow.approvalStatus !== 'draft'}
              onClick={() => void submitForApproval({ workflowDefinitionId: existingWorkflow.id }).unwrap().then(() => studioQuery.refetch())}
              variant="secondary"
            >
              Submit for Approval
            </ActionButton>
          ) : undefined}
        >
          <KeyValueGrid
            items={[
              { label: 'Can Edit', value: studioState?.viewerAuthority.canEdit ? 'Yes' : 'No' },
              { label: 'Can Review', value: studioState?.viewerAuthority.canReview ? 'Yes' : 'No' },
              { label: 'Can Run', value: studioState?.viewerAuthority.canRun ? 'Yes' : 'No' },
              { label: 'Google Workspace', value: studioState?.connectorReadiness.googleWorkspace.status ?? 'not_connected' },
            ]}
          />
        </SettingsPanel>

        <SettingsPanel
          eyebrow="Canonical Proof"
          title="Admissions Proof Route"
          description="Workflow execution proof remains canonical on the admissions dashboard."
          actions={studioState ? (
            <ActionButton href={studioState.canonicalThread.proofRoute} variant="ghost">
              Open Proof Route
            </ActionButton>
          ) : undefined}
        >
          <KeyValueGrid
            items={[
              { label: 'Proof Route', value: studioState?.canonicalThread.proofRoute ?? '/school/admissions/dashboard' },
              { label: 'Launch Route', value: studioState?.canonicalThread.launchRoute ?? '/school/admissions/dashboard' },
              { label: 'Latest Run', value: studioState?.canonicalThread.latestRunId ?? 'None yet' },
            ]}
          />
        </SettingsPanel>
      </div>

      {studioState?.viewerAuthority.canReview && existingWorkflow ? (
        <SettingsPanel
          eyebrow="Moderation"
          title="Review Workflow"
          description="Moderation remains explicit; autonomous delivery is granted only through review."
          actions={(
            <>
              <ActionButton
                data-testid="assistant-workflow-approve-button"
                disabled={pending}
                onClick={() => void reviewWorkflow({
                  workflowDefinitionId: existingWorkflow.id,
                  decision: 'approved',
                  comment: reviewComment.trim() || undefined,
                  autonomousDelivery: true,
                }).unwrap().then(() => studioQuery.refetch())}
                variant="primary"
              >
                Approve
              </ActionButton>
              <ActionButton
                data-testid="assistant-workflow-reject-button"
                disabled={pending}
                onClick={() => void reviewWorkflow({
                  workflowDefinitionId: existingWorkflow.id,
                  decision: 'rejected',
                  comment: reviewComment.trim() || undefined,
                  autonomousDelivery: false,
                }).unwrap().then(() => studioQuery.refetch())}
                variant="danger"
              >
                Reject
              </ActionButton>
            </>
          )}
        >
          <Field label="Review Comment">
            <TextArea
              data-testid="assistant-workflow-review-comment-input"
              onChange={(event) => setReviewComment(event.target.value)}
              value={reviewComment}
            />
          </Field>
        </SettingsPanel>
      ) : null}

      <SettingsPanel
        eyebrow="Workflow Graph"
        title="Current Graph Summary"
        description="The first live package-owned version exposes the compiled graph and execution surfaces before a richer visual canvas is reintroduced."
      >
        <DataTable
          columns={['Node', 'Kind']}
          rows={((existingWorkflow?.graphSpec.nodes ?? selectedTemplate?.defaultGraphSpec.nodes ?? []) as AssistantWorkflowGraphNode[]).map((node) => [
            node.label,
            <StatusPill key={`${node.id}-kind`} tone="neutral">{node.kind}</StatusPill>,
          ])}
        />
      </SettingsPanel>

      <SettingsPanel
        eyebrow="Executions"
        title="Execution History"
        description="Live execution history for the selected workflow definition."
      >
        {studioState?.executions.length ? (
          <DataTable
            columns={['Execution', 'Status', 'Run', 'Summary']}
            rows={studioState.executions.map((execution: IntellioAssistantWorkflowExecutionRecord) => [
              execution.id,
              <StatusPill key={`${execution.id}-status`} tone={execution.status === 'failed' ? 'error' : execution.status === 'blocked' ? 'warning' : 'info'}>
                {execution.status}
              </StatusPill>,
              execution.runId ?? 'n/a',
              execution.resultSummary ?? execution.errorMessage ?? 'No summary available',
            ])}
          />
        ) : (
          <Notice title="No executions yet">Run the workflow once to populate execution history.</Notice>
        )}
      </SettingsPanel>
    </section>
  );
};
